import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  TextField,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import PersistentDataGrid from "../../components/table/PersistentDataGrid";
import { GridToolbar } from "@mui/x-data-grid";
import { usePaginationState } from "../../hooks/usePaginationState";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import ServicingApi from "../../services/AssociatedServices";
import ModalForm from "../delivery/ModalForm";
import StepForm from "../delivery/StepForm";
import { useDispatch, useSelector } from "react-redux";
import { setShowMessage } from "../../redux/slices/notificationSlice";
import ServiceModal from "./ServiceModal";
import translations from "../../assets/locals/translations";

const ServiceDue = () => {
  const dispatch = useDispatch();
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations['en'];
  const Services = new ServicingApi();
  const [stockData, setStockData] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paginationModel, handlePaginationModelChange] = usePaginationState('servicesDuePaginationState', { pageSize: 50, page: 0 });
  const [isModalOpen, setIsModalOpen] = useState({ modal: false, data: null });
  const [stepIndex, setStepIndex] = useState(10);
  const [allStepsData, setAllStepsData] = useState({});

  const theme = useTheme();
  // Adjust grid container based on screen size for responsiveness
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Global search state
  const [searchQuery, setSearchQuery] = useState("");

  const getTodayDueService = async () => {
    setLoading(true);
    try {
      const response = await Services.getDueServicesToday();
      if (response && response.isSuccess) {
        const rowData = response.result.map((item, index) => ({
          ...item,
          no: index + 1,
        }));
        setStockData(rowData);
      }
    } catch (error) {
      console.error(error, "Error getting stocks list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTodayDueService();
  }, []);

  const columns = [
    {
      field: "Menu",
      headerName: t.followUp || "Follow Up",
      flex: 1,
      minWidth: 50,
      renderCell: (params) => (
        <Tooltip title={t.followUp || "Follow Up"}>
          <button
            onClick={() => handleOpenModal(params.row)}
            className="myActionBtn d-flex align-items-center"
            style={{ cursor: "pointer" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              fill="currentColor"
              className="bi bi-plus-circle text-primary"
              viewBox="0 0 16 16"
            >
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
            </svg>
          </button>
        </Tooltip>
      ),
    },
    { field: "no", headerName: t.no || "No", flex: 1, minWidth: 70 },
    {
      field: "last_hour",
      headerName: t.lastServiceHours || "Last Service Hours",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            textAlign: 'center',
            pl: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: "scheduled_services",
      headerName: t.scheduledService || "Scheduled Service",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const value = params.value || "";
        return (
          <Tooltip title={value}>
            <span>{value.toUpperCase()}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "customer_name",
      headerName: t.customerName || "Customer Name",
      flex: 1,
      minWidth: 310,
      renderCell: (params) => (
        <span>{params.value ? params.value.toUpperCase() : ""}</span>
      ),
    },
    {
      field: "village",
      headerName: t.village || "Village",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <span>{params.value ? params.value.toUpperCase() : ""}</span>
      ),
    },
    {
      field: "phone_number",
      headerName: t.phoneNumber || "Phone Number",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <span>{params.value ? params.value.toUpperCase() : ""}</span>
      ),
    },
    {
      field: "due_date",
      headerName: t.dueDate || "Due Date",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = moment(params.value);
        return date.isValid() ? date.format("DD-MM-YYYY") : "";
      },
    },
    {
      field: "schedule_notes",
      headerName: t.scheduleNote || "Schedule Note",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "last_service_notes",
      headerName: t.lastServiceNote || "last Service Note",
      flex: 1,
      minWidth: 200,
    },
    { field: "model_name", headerName: t.modal || "MODAL", flex: 1, minWidth: 180 },
    { field: "chassis_no", headerName: t.chassis || "CHASSIS", flex: 1, minWidth: 200 },
    { field: "engine_no", headerName: t.engine || "ENGINE", flex: 1, minWidth: 200 },
    { field: "total_hour", headerName: t.totalHours || "Total Hours", flex: 1, minWidth: 150 },
  ];

  const handlePageSizeChange = (newPageSize) => {
    handlePaginationModelChange({ ...paginationModel, pageSize: newPageSize });
  };

  const handleOpenModal = (row) => {
    setIsModalOpen({ modal: true, data: row });
    setAllStepsData({
      last_hours: row.last_hour,
      customer_name: row.customer_name,
      phone_number: row.phone_number,
      service_name: row.service_name,
      village: row.village,
      chassis_no: row.chassis_no,
      model_name: row.model_name,
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen({ modal: false, data: null });
  };

  const handleSaveStepData = async (values) => {
    console.log(`Saving values for step`, values, isModalOpen.data);

    const payload = {
      ...values,
      service_due_id: isModalOpen.data.service_due_id,
      service_id: isModalOpen.data.service_id,
      delivery_id: isModalOpen.data.delivery_id,
      model_id: isModalOpen.data.model_id,
      customer_id: isModalOpen.data.customer_id,
    };
    try {
      if (values && values.tab && values.tab === "service_hours") {
        const lastH = parseInt(payload.last_service_hour, 10);
        // check for missing or zero
        if (isNaN(lastH) || lastH === 0) {
          dispatch(setShowMessage("Please enter the current service hours!"));
          return;
        }
        payload.last_hours = lastH;
        // payload.total_hours = totalH;
        const response = await Services.updateLastServices(payload);
        if (response && response.isSuccess) {
          dispatch(setShowMessage(response.message));
          await getTodayDueService();
          handleCloseModal();
        }

        // Completed block
      } else if (values && values.tab && values.tab === "schedule_followup") {
        // Next block
        if (!payload.next_followup_date) {
          dispatch(setShowMessage("Please select the next followup date!"));
          return;
        }
        const response = await Services.createNextServiceFollowUp(payload);
        if (response && response.isSuccess) {
          dispatch(setShowMessage(response.message));
          await getTodayDueService();
          handleCloseModal();
        }
      } else if (values && values.tab && values.tab === "actual_hours") {
        payload.actual_hours = parseInt(values.actual_hours) || 0;
        const response = await Services.updateActualHours(payload);
        if (response && response.isSuccess) {
          dispatch(setShowMessage(response.message));
          await getTodayDueService();
          handleCloseModal();
        }
      } else if (values && values.tab && values.tab === "tractor_status") {
        payload.has_tractor = values.has_tractor;
        const response = await Services.submitTractorStatus(payload);
        if (response && response.isSuccess) {
          dispatch(setShowMessage(response.message));
          await getTodayDueService();
          handleCloseModal();
        }
      }
    } catch (error) {
      console.error("Submission Error:", error);
    }
  };

  // Filter rows based on search query across multiple fields
  const filteredRows = useMemo(() => {
    if (!searchQuery) return stockData;
    return stockData.filter((row) =>
      Object.keys(row).some((field) =>
        String(row[field]).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, stockData]);

  return (
    <>
      <div className="bg-white p-2">
        {/* Header with title and global search input */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >

          <div>
            <h3 className="text-2xl font-bold">{t.servicesDue || "Services Due"}</h3>
          </div>

          <TextField
            variant="outlined"
            placeholder={t.globalSearch || "Global Search..."}
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: isMobile ? "100%" : 300 }}
          />

        </Box>

        <div
          className="tableMenuHover"
          style={{ flexGrow: 1, width: "100%", height: "calc(100vh - 70px)" }}
        >
          <PersistentDataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={(row) => row.no}
            pagination
            paginationMode="client"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[10, 15, 25, 50]}
            rowsPerPageOptions={[10, 20, 25, 50, 100]}
            disableSelectionOnClick
            autoHeight={isMobile}
            getRowClassName={(params) => {
              const dueDate = moment(params.row.due_date);
              if (
                dueDate.isValid() &&
                dueDate.isBefore(moment(), "day") &&
                params.row.status !== "Completed"
              ) {
                return "overdue-text";
              }
              return "";
            }}
            components={{
              Toolbar: GridToolbar,
              NoRowsOverlay: () => (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                  }}
                >
                  <span style={{ fontFamily: "Poppins" }}>
                    {t.noDataAvailable || "No Data Available"}
                  </span>
                </div>
              ),
            }}
            componentsProps={{
              toolbar: {
                position: "right",
                style: { fontFamily: "Poppins", alignSelf: "end" },
              },
            }}
            sx={{
              width: "100%",
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "uppercase",
              },
            }}
            loading={loading}
          />
        </div>
      </div>
      <ServiceModal
        show={isModalOpen.modal}
        onHide={handleCloseModal}
        initialData={allStepsData}
        onSave={handleSaveStepData}
      />
    </>
  );
};

export default ServiceDue;
